import {
  ActivityFilterKey,
  ActivityInboxType,
  AssetChatFeedItem,
  AssetChatSideChat,
  AssetChatSubject,
  AssetChatStreamFilter,
  MediaPickerComponent,
  PermissionValues,
  useAssetChatFeed,
  useAssetChatSideChats,
  useCurrentAccount,
  useFindUnreadActivityInbox,
} from '@gripp/shared-logic';
import { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react';
import { QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FlatList, View } from 'react-native';
import { Button } from '../crop-circle/Button';
import { EmptyState } from '../feedback/EmptyState';
import { ErrorState } from '../feedback/ErrorState';
import { LoadingIndicator } from '../loadingIndicator';
import { spacing } from '../crop-circle/tokens';
import { AnonymousId, FunctionId } from '../issue/issueFormatUtils';
import { AssetChatComposer } from './assetChatComposer';
import { AssetChatRow } from './assetChatRow';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

/**
 * What a feed row can do beyond opening its thread. Grouped into one object
 * rather than five props per level because every one of them needs something
 * shared-ui does not have — web navigation, the clipboard, the call provider,
 * the share panel — so they are all supplied by the host and only routed
 * through here. An absent handler hides its menu entry (see
 * `MessageActionMenu`), which is how the menu varies per surface.
 */
export type AssetChatRowActions = {
  onOpenSideChat?: (item: AssetChatFeedItem, parentMessageId: string) => void;
  /**
   * Starts a side chat on the row's origin message. Receives the side chats
   * the row already knows about so the create can no-op idempotently rather
   * than minting a second "Side chat started from…" divider.
   */
  onStartSideChat?: (
    item: AssetChatFeedItem,
    existing: AssetChatSideChat[]
  ) => void;
  onCall?: (item: AssetChatFeedItem) => void;
  onCopy?: (item: AssetChatFeedItem) => void;
  onShare?: (item: AssetChatFeedItem) => void;
};

export type AssetChatFeedListProps = {
  items: AssetChatFeedItem[];
  isFiltered?: boolean;
  currentAccountId?: string;
  isFetchingNextPage?: boolean;
  /** False once the oldest page has loaded, which is when `listEndNotice` shows. */
  hasNextPage?: boolean;
  /** Shown above the oldest message once all history has loaded. */
  listEndNotice?: ReactElement | null;
  onEndReached?: () => void;
  /** Opens the row's Issue thread from its "N replies" affordance. */
  onOpenThread: (item: AssetChatFeedItem) => void;
  /** Reuses the activity list's unread decoration. Defaults to "never unread". */
  isUnread?: (item: AssetChatFeedItem) => boolean;
  /** Thread-wide side chats per Issue id, from `useAssetChatSideChats`. */
  sideChatsByIssue?: Map<string, AssetChatSideChat[]>;
  rowActions?: AssetChatRowActions;
  /**
   * False while the side-chat summaries are still loading. Starting one then
   * would hand `useStartSideChat` an empty map, and its idempotency guard
   * reads that as "none exists" — so an origin that already had a side chat
   * would get a second "Side chat started from…" divider. Defaults to true
   * for a caller that supplies no side-chat data at all.
   */
  canStartSideChat?: boolean;
  testID?: string;
};

/**
 * The feed's presentation: an inverted `FlatList` over already-fetched
 * `items`, newest message at the bottom, paging into older history as the
 * list scrolls up. No hooks beyond React Native's own — kept separate from
 * `AssetChatFeed` (below) so a story or spec can drive it with plain data
 * instead of mocking `useAssetChatFeed`.
 *
 * Not `InfiniteDataGrid` — see
 * spec/technical-plans/0035-asset-conversation-mode.md (Phase 1) for why: it
 * is a `react-native-paper` `DataTable` in nested `ScrollView`s with no
 * virtualization and no `inverted` support.
 */
export const AssetChatFeedList = ({
  items,
  isFiltered = false,
  currentAccountId,
  isFetchingNextPage,
  hasNextPage = true,
  listEndNotice,
  onEndReached,
  onOpenThread,
  isUnread,
  sideChatsByIssue,
  rowActions,
  canStartSideChat = true,
  testID,
}: AssetChatFeedListProps) => {
  const { t } = useTranslation();

  /**
   * Same rule `issueMessage.tsx` applies to its own call affordance: you
   * cannot ring yourself, the Anonymous placeholder, or the system account. A
   * row whose author fails it gets no Call entry rather than one that dials
   * nothing.
   */
  const callableAuthor = (item: AssetChatFeedItem) => {
    const author = item.originMessage?.createdBy ?? item.createdBy;
    if (!author?.id) return false;
    if (author.id === AnonymousId || author.id === FunctionId) return false;
    return author.id !== currentAccountId;
  };

  return (
    <View style={styles.container} testID={testID}>
      <FlatList
        style={styles.container}
        data={items}
        keyExtractor={(item) => item.id}
        inverted
        onEndReached={onEndReached}
        // 'on-drag' rather than iOS's 'interactive': the list is inverted, and
        // interactive dismissal tracks the gesture in the flipped coordinate
        // space, so the keyboard would follow the finger the wrong way.
        keyboardDismissMode="on-drag"
        ListEmptyComponent={
          <View style={styles.empty}>
            <EmptyState
              title={t(
                isFiltered
                  ? 'chat.feed.filteredEmptyTitle'
                  : 'chat.feed.emptyTitle'
              )}
            />
          </View>
        }
        ListFooterComponent={
          isFetchingNextPage ? (
            <LoadingIndicator size={24} />
          ) : !hasNextPage && items.length > 0 ? (
            listEndNotice
          ) : null
        }
        renderItem={({ item }) => (
          <AssetChatRow
            item={item}
            currentAccountId={currentAccountId}
            onOpenThread={() => onOpenThread(item)}
            isUnread={isUnread?.(item) ?? false}
            sideChats={sideChatsByIssue?.get(item.id)}
            onOpenSideChat={
              rowActions?.onOpenSideChat
                ? (parentMessageId) =>
                    rowActions.onOpenSideChat?.(item, parentMessageId)
                : undefined
            }
            onStartSideChat={
              rowActions?.onStartSideChat && canStartSideChat
                ? () =>
                    rowActions.onStartSideChat?.(
                      item,
                      sideChatsByIssue?.get(item.id) ?? []
                    )
                : undefined
            }
            onCall={
              rowActions?.onCall && callableAuthor(item)
                ? () => rowActions.onCall?.(item)
                : undefined
            }
            onCopy={
              rowActions?.onCopy ? () => rowActions.onCopy?.(item) : undefined
            }
            onShare={
              rowActions?.onShare ? () => rowActions.onShare?.(item) : undefined
            }
            testID={testID ? `${testID}-row-${item.id}` : undefined}
          />
        )}
      />
    </View>
  );
};

export type AssetChatFeedProps = {
  subject: AssetChatSubject;
  filterKey: ActivityFilterKey;
  streamFilter?: AssetChatStreamFilter;
  /** Opens the row's Issue thread from its "N replies" affordance. */
  onOpenThread: (item: AssetChatFeedItem) => void;
  /**
   * The platform media picker, forwarded to the composer. Omit it and the
   * feed is read-only — which is what the unread-badge caller wants, and what
   * PR 1 shipped.
   */
  mediaPicker?: MediaPickerComponent;
  enabled?: boolean;
  /** Native host revalidated admission; refresh data without replacing the list. */
  refreshRevision?: number;
  renderComposer?: (props: {
    subject: AssetChatSubject;
    feedCacheKey?: QueryKey;
  }) => ReactNode;
  rowActions?: AssetChatRowActions;
  /** Shown above the oldest message once all history has loaded. */
  listEndNotice?: ReactElement | null;
  testID?: string;
};

/**
 * The asset's chat: wraps `useAssetChatFeed` (subject resolution, workspace
 * pinning, ordering — see that hook) and hands its output to
 * `AssetChatFeedList`.
 */
export const AssetChatFeed = ({
  subject,
  filterKey,
  streamFilter = 'all',
  enabled = true,
  refreshRevision = 0,
  renderComposer,
  onOpenThread,
  mediaPicker,
  rowActions,
  listEndNotice,
  testID,
}: AssetChatFeedProps) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();

  // The chat route is gated on issue.read, but posting goes through
  // `reportIssue`, which needs issue.create. Without this a read-only member
  // could compose a whole message and get FORBIDDEN on send — the dead end
  // AGENTS.md's "match the frontend gate to the resolver" rule exists to stop.
  const canPost = !!account?.hasPermission(PermissionValues.issue.create);

  const {
    items,
    cacheKey,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useAssetChatFeed({ ...subject, filterKey, streamFilter, enabled });

  // One request for the whole loaded page, not one per row — a row is an
  // Issue and the pills are thread-wide, so per-row queries would be 25-50
  // concurrent 1000-row reads on a real asset. See `useAssetChatSideChats`.
  const issueIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    sideChatsByIssue,
    isLoading: isLoadingSideChats,
    refetch: refetchSideChats,
  } = useAssetChatSideChats(issueIds, {
    enabled: enabled && !!rowActions?.onOpenSideChat,
  });

  // Same cross-reference `activityList.tsx`'s `hasUnread` does for the
  // classic list: an ActivityInbox row for this Issue that the viewer hasn't
  // read yet. `createInboxForMessage` already excludes the poster, so this
  // can never mark the viewer's own row unread.
  const { data: inboxData, refetch: refetchInbox } = useFindUnreadActivityInbox(
    { enabled }
  );
  const refreshData = useRef<(() => Promise<void>) | undefined>(undefined);
  refreshData.current = async () => {
    // Re-enabling admission may already have started a stale-query refresh.
    // Reuse that request rather than cancelling it and issuing a second one.
    const options = { cancelRefetch: false, throwOnError: true };
    await Promise.all([
      refetch(options),
      refetchInbox(options),
      ...(rowActions?.onOpenSideChat && issueIds.length
        ? [refetchSideChats(options)]
        : []),
    ]);
  };
  const refreshedRevision = useRef(refreshRevision);
  useEffect(() => {
    if (!enabled || refreshedRevision.current === refreshRevision) return;
    refreshedRevision.current = refreshRevision;
    refreshData.current?.().catch(console.error);
  }, [enabled, refreshRevision]);
  const isUnread = useMemo(() => {
    const inboxItems: { type?: ActivityInboxType; data?: { id?: string } }[] =
      inboxData?.items ?? [];
    const unreadIssueIds = new Set(
      inboxItems
        .filter(
          (i) =>
            i.type === ActivityInboxType.IssueUpdated ||
            i.type === ActivityInboxType.IssueMessageCreated
        )
        .map((i) => i.data?.id)
    );
    return (item: AssetChatFeedItem) => unreadIssueIds.has(item.id);
  }, [inboxData]);

  const feedContent = isLoading ? (
    <LoadingIndicator flexContainer />
  ) : isError ? (
    <ErrorState
      title={t('chat.feed.errorTitle')}
      action={
        <Button
          size="default"
          onPress={async () => {
            if (!enabled) return;
            try {
              await refetch();
            } catch (error) {
              console.error(error);
            }
          }}
          testID={testID ? `${testID}-error-retry` : undefined}
        >
          {t('chat.feed.retry')}
        </Button>
      }
      testID={testID ? `${testID}-error` : undefined}
    />
  ) : (
    <AssetChatFeedList
      key={streamFilter}
      items={items}
      isFiltered={streamFilter !== 'all'}
      currentAccountId={account?.id}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      listEndNotice={listEndNotice}
      onEndReached={async () => {
        if (!enabled || !hasNextPage || isFetchingNextPage) return;
        try {
          await fetchNextPage();
        } catch (error) {
          console.error(error);
        }
      }}
      onOpenThread={onOpenThread}
      isUnread={isUnread}
      sideChatsByIssue={sideChatsByIssue}
      rowActions={rowActions}
      canStartSideChat={!isLoadingSideChats}
      testID={testID}
    />
  );

  return (
    // The list keeps flex:1 above a composer sized by its own content, so the
    // composer stays pinned to the bottom edge and the inverted list's newest
    // row sits directly on top of it.
    <View style={styles.stack}>
      {feedContent}
      {canPost &&
        (renderComposer ? (
          renderComposer({
            subject,
            feedCacheKey: streamFilter === 'all' ? cacheKey : undefined,
          })
        ) : mediaPicker ? (
          <AssetChatComposer
            subject={subject}
            assetName={subject.workspaceAsset?.asset?.name}
            // Narrowed streams wait for server filtering: the composer can
            // send a different type/status than the selection currently shows.
            feedCacheKey={streamFilter === 'all' ? cacheKey : undefined}
            mediaPicker={mediaPicker}
            testID={testID ? `${testID}-composer` : undefined}
          />
        ) : null)}
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  stack: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: role.canvas,
  },
  empty: {
    // Inverted list: rotate the empty state back upright so its content
    // reads normally instead of upside down.
    transform: [{ scaleY: -1 }],
    paddingVertical: spacing['2xl'],
  },
}));
